import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';

import {
    Box,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TablePagination,
    TableRow,
    TableSortLabel,
    Typography
} from '@mui/material';
import { visuallyHidden } from '@mui/utils';

import {
    DEFAULT_TABLE_ROW_OPTIONS,
    DEFAULT_TABLE_ROWS_PER_PAGE
} from '../../../../utils/formValidation/commonConstants';
import { stableSort } from '../../../../utils/sortFunctions';

import SubmissionPoolTableRow from './SubmissionPoolTableRow';

const headCells = [
    { id: 'ptCode', numeric: false, label: 'ID' },
    { id: 'ptName', numeric: false, label: 'Participant Name' },
    { id: 'cvName', numeric: false, label: 'CV Name' },
    { id: 'jobSectorName', numeric: false, label: 'Job Sector' },
    { id: 'submit', numeric: false, label: 'Submit' }
];

const SubmissionPoolTable = ({ rows, roles, searchTerm, onRowChange }) => {
    const [order, setOrder] = useState('asc');
    const [orderBy, setOrderBy] = useState('ptName');
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(DEFAULT_TABLE_ROWS_PER_PAGE);

    // First time ensure data is sorted by id
    useEffect(() => {
        setOrder('asc');
        setOrderBy('ptName');
    }, []);

    useEffect(() => {
        setPage(0);
    }, [searchTerm]);

    // HELPER FNS
    const onSort = (property) => {
        const isAsc = orderBy === property && order === 'asc';
        setOrder(isAsc ? 'desc' : 'asc');
        setOrderBy(property);
        setPage(0);
    };

    const onChangePage = (e, newPage) => {
        const page = newPage > 0 ? newPage : 0;
        setPage(page);
    };

    const onChangeRowsPerPage = (e) => {
        setRowsPerPage(parseInt(e.target.value, 10));
        setPage(0);
    };

    // RENDER
    return (
        <Box sx={{ width: '100%' }}>
            <Paper sx={{ width: '100%', mb: 2 }}>
                <TableContainer>
                    <Table sx={{ minWidth: 640 }} aria-labelledby="tableTitle" size="medium">
                        <TableHead>
                            <TableRow>
                                {headCells.map((headCell) => (
                                    <TableCell
                                        key={headCell.id}
                                        sortDirection={orderBy === headCell.id ? order : false}>
                                        <TableSortLabel
                                            active={orderBy === headCell.id}
                                            direction={orderBy === headCell.id ? order : 'asc'}
                                            onClick={() => onSort(headCell.id)}>
                                            <Typography variant={'subtitle3'} color={'primary'}>
                                                {headCell.label}
                                            </Typography>
                                            {orderBy === headCell.id ? (
                                                <Box component="span" sx={visuallyHidden}>
                                                    {order === 'desc'
                                                        ? 'sorted descending'
                                                        : 'sorted ascending'}
                                                </Box>
                                            ) : null}
                                        </TableSortLabel>
                                    </TableCell>
                                ))}
                            </TableRow>
                        </TableHead>

                        <TableBody>
                            {stableSort(rows, orderBy, order)
                                .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                                .map((row) => (
                                    <SubmissionPoolTableRow
                                        key={row.id}
                                        row={row}
                                        roles={roles}
                                        onRowChange={onRowChange}
                                    />
                                ))}
                        </TableBody>
                    </Table>
                </TableContainer>
                <TablePagination
                    data-testid="tablePagination"
                    rowsPerPageOptions={DEFAULT_TABLE_ROW_OPTIONS}
                    component="div"
                    count={rows.length}
                    rowsPerPage={rowsPerPage}
                    page={page}
                    onPageChange={onChangePage}
                    onRowsPerPageChange={onChangeRowsPerPage}
                />
            </Paper>
        </Box>
    );
};

SubmissionPoolTable.propTypes = {
    rows: PropTypes.array.isRequired,
    roles: PropTypes.array,
    searchTerm: PropTypes.string,
    onRowChange: PropTypes.func
};

export default SubmissionPoolTable;
